// src/components/auth/BusinessSsoGate.jsx
//
// Consumes a `?token=` (SSO) parameter at the app root, issued by the personal
// app (paymeo-webapp) when the user switches to their business account.
//
// Flow:
//   1. Read `?token=` (only at `/` — shared-product links are handled by
//      ShareProductPage for the cart's personal login).
//   2. Validate it against the business backend (/isLoggedIn) and store the
//      session (`_t`, userData, accountDetails) via the auth context.
//   3. Strip the token from the URL so it doesn't linger in history/referrers.
//   4. Route the user:
//        - new businesses without a business name -> BusinessNameModal
//        - new businesses without a market        -> /setup/select-market
//        - everyone else                          -> /dashboard
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../utils/api';
import { API_CONFIG } from '../../utils/apiConfig';
import BusinessNameModal from './BusinessNameModal';

const BusinessSsoGate = ({ children }) => {
  const { currentUser, loading, setUserData } = useAuth();
  const navigate = useNavigate();
  const [processing, setProcessing] = useState(false);
  const [showNameModal, setShowNameModal] = useState(false);
  const handledRef = useRef(false);

  useEffect(() => {
    if (handledRef.current || typeof window === 'undefined') return;
    if (window.location.pathname !== '/') return;

    const url = new URL(window.location.href);
    const token = url.searchParams.get('token');
    if (!token) return;

    handledRef.current = true;
    setProcessing(true);

    (async () => {
      try {
        // The api interceptor attaches `_t` as the Bearer token, so store it
        // before validating.
        localStorage.setItem('_t', token);

        const { data } = await api.get(API_CONFIG.AUTH.IS_LOGGED_IN);
        if (!data || !data.authenticated || !data.user) {
          throw new Error('Invalid business token');
        }

        const { user, account } = data;
        setUserData(user, account, token);

        const needsName = !user.company_name || user.company_name === user.fullname;
        const isNew = !user.market_setup_done && !user.has_skipped_setup && user.hasProduct === 0;

        if (needsName && isNew) {
          setShowNameModal(true);
        } else if (isNew) {
          navigate('/setup/select-market', { replace: true });
        } else {
          navigate('/dashboard', { replace: true });
        }
      } catch (error) {
        // Not a valid business token — fall back to the plain page (a
        // personal token on this URL is handled by ShareProductPage routes).
        console.error('[Business SSO] Invalid token:', error);
        localStorage.removeItem('_t');
      } finally {
        const clean = new URL(window.location.href);
        clean.searchParams.delete('token');
        clean.searchParams.delete('user');
        window.history.replaceState({}, document.title, clean.toString());
        setProcessing(false);
      }
    })();
  }, [navigate, setUserData]);

  if (processing) {
    return (
      <div className="fixed inset-0 flex flex-col items-center justify-center z-[9999] bg-gradient-to-b from-blue-600 to-blue-500">
        <img
          src="https://cdn.paymeo.co/uploads/brand/1788303485685_b61c6298a899909071.png"
          alt="Paymeo Logo"
          className="w-20 h-20 animate-heartbeat select-none"
        />
        <p className="mt-4 text-white text-sm font-medium">Switching to your business account...</p>
      </div>
    );
  }

  return (
    <>
      {children}
      <BusinessNameModal
        show={showNameModal}
        user={currentUser}
        onComplete={() => {
          setShowNameModal(false);
          navigate('/setup/select-market', { replace: true });
        }}
        onClose={() => {
          setShowNameModal(false);
          navigate('/dashboard', { replace: true });
        }}
      />
    </>
  );
};

export default BusinessSsoGate;
